/**
 * FaqAdminPage — CMS for help-center articles at /support/faqs.
 *
 * List on the left, editor on the right. Clicking an article loads its full
 * body into the form. "New article" clears the form. Publish toggle is a
 * single field on the form — toggling it on stamps `publishedAt` server-side.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ChevronLeft, FileText, PlusCircle, Trash2 } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';
import {
  createFaqArticle,
  deleteFaqArticle,
  listFaqArticles,
  updateFaqArticle,
  type AdminFaqArticle,
  type AdminFaqCategory,
} from '@/src/infrastructure/lib/api';
import { FAQ_CATEGORIES, FAQ_CATEGORY_LABEL } from './constants';

interface FormState {
  id: string | null;
  title: string;
  slug: string;
  category: AdminFaqCategory;
  content: string;
  excerpt: string;
  tags: string;
  sortOrder: number;
  isPublished: boolean;
}

const EMPTY_FORM: FormState = {
  id: null,
  title: '',
  slug: '',
  category: 'GETTING_STARTED',
  content: '',
  excerpt: '',
  tags: '',
  sortOrder: 0,
  isPublished: false,
};

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function FaqAdminPage() {
  const [items, setItems] = useState<AdminFaqArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [filter, setFilter] = useState<AdminFaqCategory | ''>('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const page = await listFaqArticles({ limit: 100, category: filter || undefined });
      setItems(page.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load articles');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function openArticle(a: AdminFaqArticle) {
    setForm({
      id: a.id,
      title: a.title,
      slug: a.slug,
      category: a.category,
      content: a.content,
      excerpt: a.excerpt ?? '',
      tags: a.tags.join(', '),
      sortOrder: a.sortOrder,
      isPublished: a.isPublished,
    });
  }

  async function save() {
    setSaving(true);
    setError('');
    const payload = {
      title: form.title,
      slug: form.slug || slugify(form.title),
      category: form.category,
      content: form.content,
      excerpt: form.excerpt || undefined,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      sortOrder: form.sortOrder,
      isPublished: form.isPublished,
    };
    try {
      if (form.id) {
        const updated = await updateFaqArticle(form.id, payload);
        setItems((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      } else {
        const created = await createFaqArticle(payload);
        setItems((prev) => [created, ...prev]);
        setForm({ ...form, id: created.id });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!form.id) return;
    if (!window.confirm('Delete this article? This cannot be undone.')) return;
    setSaving(true);
    try {
      await deleteFaqArticle(form.id);
      setItems((prev) => prev.filter((a) => a.id !== form.id));
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  const filteredItems = useMemo(() => items, [items]);

  return (
    <div className="p-6 md:p-8 space-y-4">
      <Link
        to="/support"
        className="inline-flex items-center gap-1 text-xs font-semibold text-on-surface-variant hover:text-on-surface"
      >
        <ChevronLeft className="w-4 h-4" /> Support tickets
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold text-on-surface flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Help articles
          </h1>
          <p className="text-sm text-on-surface-variant">
            Public FAQ &amp; help-center content. Published articles appear at
            <code className="text-[10px] px-1 bg-surface-low rounded">/dashboard/help</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setForm(EMPTY_FORM)}
          className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-90"
        >
          <PlusCircle className="w-4 h-4" />
          New article
        </button>
      </header>

      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-4">
        {/* List */}
        <aside className="space-y-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as AdminFaqCategory | '')}
            className="w-full rounded-full border border-border-subtle bg-surface px-3 py-1.5 text-xs"
          >
            <option value="">All categories</option>
            {FAQ_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {FAQ_CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>

          {loading ? (
            <p className="text-xs text-on-surface-variant">Loading…</p>
          ) : (
            <ul className="space-y-1 max-h-[70vh] overflow-auto pr-1">
              {filteredItems.map((a) => (
                <li key={a.id}>
                  <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 2 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => openArticle(a)}
                    className={cn(
                      'w-full text-left rounded-xl border border-border-subtle p-3 transition-colors',
                      form.id === a.id
                        ? 'border-primary/40 bg-primary/5'
                        : 'hover:border-primary/30 hover:bg-surface-low/40',
                    )}
                  >
                    <p className="text-[10px] uppercase tracking-wider text-on-surface-variant font-semibold">
                      {FAQ_CATEGORY_LABEL[a.category]}
                      {!a.isPublished && (
                        <span className="ml-2 text-amber-600">DRAFT</span>
                      )}
                    </p>
                    <p className="text-sm font-semibold text-on-surface line-clamp-2">
                      {a.title}
                    </p>
                    <p className="text-[10px] text-on-surface-variant">
                      {a.viewCount} views · {a.helpfulCount}👍 / {a.notHelpfulCount}👎
                    </p>
                  </motion.button>
                </li>
              ))}
              {filteredItems.length === 0 && (
                <li className="text-center text-xs text-on-surface-variant py-4">
                  No articles yet.
                </li>
              )}
            </ul>
          )}
        </aside>

        {/* Editor */}
        <section className="rounded-3xl border border-border-subtle bg-surface-low/40 p-5 space-y-4">
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-on-surface">
              {form.id ? 'Edit article' : 'New article'}
            </h2>
            {form.id && (
              <button
                type="button"
                onClick={remove}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 text-rose-600 px-3 py-1 text-[11px] font-semibold hover:bg-rose-500/10"
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </button>
            )}
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title">
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="form-input"
              />
            </Field>
            <Field label="Slug">
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                onFocus={() => {
                  if (!form.slug && form.title) {
                    setForm({ ...form, slug: slugify(form.title) });
                  }
                }}
                className="form-input font-mono text-xs"
              />
            </Field>
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value as AdminFaqCategory })
                }
                className="form-input"
              >
                {FAQ_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {FAQ_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Sort order">
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm({ ...form, sortOrder: Number(e.target.value) || 0 })
                }
                className="form-input"
              />
            </Field>
            <Field label="Tags (comma separated)" className="md:col-span-2">
              <input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                className="form-input"
                placeholder="getting-started, publishing"
              />
            </Field>
            <Field label="Excerpt (preview text)" className="md:col-span-2">
              <input
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="form-input"
                maxLength={320}
              />
            </Field>
            <Field label="Content" className="md:col-span-2">
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={14}
                className="form-input font-mono text-xs"
                placeholder="Plain text or markdown source"
              />
            </Field>
          </div>

          <footer className="flex items-center justify-between border-t border-border-subtle pt-4">
            <label className="inline-flex items-center gap-2 text-xs text-on-surface-variant">
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                className="accent-primary"
              />
              Published (visible in help center)
            </label>
            <button
              type="button"
              onClick={save}
              disabled={saving || !form.title.trim() || !form.content.trim()}
              className={cn(
                'rounded-full px-5 py-2 text-xs font-semibold shadow-sm transition-colors',
                saving || !form.title.trim() || !form.content.trim()
                  ? 'bg-surface-low text-on-surface-variant cursor-not-allowed'
                  : 'bg-primary text-white hover:opacity-90',
              )}
            >
              {saving ? 'Saving…' : form.id ? 'Save changes' : 'Create article'}
            </button>
          </footer>
        </section>
      </div>

      {/* Local input styling — reuses tokens from globals. */}
      <style>{`
        .form-input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid var(--color-border-subtle, rgba(120,120,140,0.25));
          background: var(--color-surface, transparent);
          padding: 0.5rem 0.75rem;
          font-size: 0.8125rem;
        }
        .form-input:focus { outline: none; border-color: var(--color-primary, #6366f1); }
      `}</style>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block space-y-1', className)}>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-on-surface-variant">
        {label}
      </span>
      {children}
    </label>
  );
}
